import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { RelevanceBadge, StatusBadge } from "../components/Badges";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Search, Filter } from "lucide-react";

const PROVINCES = ["", "ON", "QC", "BC", "AB", "SK", "MB", "NS", "NB", "NL", "PE", "NT", "YT", "NU", "CA"];
const PLATFORMS = ["", "CanadaBuys", "MERX", "BidsAndTenders", "Bonfire", "Biddingo", "SAPAriba",
  "CustomMunicipal", "CustomSchoolBoard", "CustomHousing", "Other"];
const CATEGORIES = ["", "HR System", "Payroll System", "Payroll Services", "ERP System",
  "Oracle Implementation", "Oracle Support", "Oracle Cloud / Fusion",
  "SAP / Ariba Procurement Related", "K-12 Enterprise Systems", "Managed Services",
  "Systems Integrator / Consulting", "Non-Relevant"];
const BUYER_TYPES = ["", "Municipality", "SchoolBoard", "Education", "Housing", "Health",
  "University", "College", "CrownCorporation", "Federal", "Provincial", "Cooperative", "Other"];

const FmtDate = ({ value }) => {
  if (!value) return <span className="text-zinc-400">—</span>;
  const d = new Date(value);
  return (
    <span className="font-mono text-xs">
      {d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" })}
    </span>
  );
};

export default function Opportunities() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({
    q: "",
    active_only: true,
    province: "",
    source_platform: "",
    category: "",
    buyer_type: "",
    min_score: 0,
    sort: "relevance",
  });

  const params = useMemo(() => {
    const p = { ...filters, limit: 100 };
    Object.keys(p).forEach(k => p[k] === "" && delete p[k]);
    return p;
  }, [filters]);

  useEffect(() => {
    api.opportunities(params).then(setData).catch(console.error);
  }, [params]);

  const setF = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));

  return (
    <div className="p-8" data-testid="opportunities-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
          All opportunities
        </div>
        <h1 className="text-3xl font-bold font-[Chivo] tracking-tight">
          {data.total.toLocaleString()} opportunities
          <span className="text-zinc-400 font-normal text-xl ml-2">matched</span>
        </h1>
      </div>

      {/* Filters */}
      <div className="border border-zinc-200 bg-white p-4 mb-4" data-testid="filters-panel">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">
          <Filter size={12} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search title, buyer, scope, rfp_id…"
              value={filters.q}
              onChange={e => setF("q", e.target.value)}
              className="pl-9 rounded-sm border-zinc-300"
              data-testid="filter-search-input"
            />
          </div>
          <Select value={filters.province || "_all"} onValueChange={v => setF("province", v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-sm border-zinc-300" data-testid="filter-province"><SelectValue placeholder="Province" /></SelectTrigger>
            <SelectContent>
              {PROVINCES.map(p => <SelectItem key={p || "_all"} value={p || "_all"}>{p || "All provinces"}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.source_platform || "_all"} onValueChange={v => setF("source_platform", v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-sm border-zinc-300" data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent>
              {PLATFORMS.map(p => <SelectItem key={p || "_all"} value={p || "_all"}>{p || "All platforms"}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category || "_all"} onValueChange={v => setF("category", v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-sm border-zinc-300" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c || "_all"} value={c || "_all"}>{c || "All categories"}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.buyer_type || "_all"} onValueChange={v => setF("buyer_type", v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-sm border-zinc-300" data-testid="filter-buyertype"><SelectValue placeholder="Buyer type" /></SelectTrigger>
            <SelectContent>
              {BUYER_TYPES.map(b => <SelectItem key={b || "_all"} value={b || "_all"}>{b || "All buyer types"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <Switch
              id="active-only"
              checked={filters.active_only}
              onCheckedChange={v => setF("active_only", v)}
              data-testid="filter-active-only"
            />
            <Label htmlFor="active-only" className="text-sm">Active only</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="min-score" className="text-sm">Min. score</Label>
            <input
              id="min-score"
              type="range"
              min={0}
              max={100}
              step={5}
              value={filters.min_score}
              onChange={e => setF("min_score", Number(e.target.value))}
              className="w-32"
              data-testid="filter-min-score"
            />
            <span className="font-mono text-xs w-8">{filters.min_score}</span>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Sort</Label>
            <Select value={filters.sort} onValueChange={v => setF("sort", v)}>
              <SelectTrigger className="rounded-sm border-zinc-300 w-40" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="closing">Closing soon</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="opportunities-table">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <th className="text-left px-4 py-2 font-medium">Score</th>
              <th className="text-left px-3 py-2 font-medium">Title / Buyer</th>
              <th className="text-left px-3 py-2 font-medium">Platform</th>
              <th className="text-left px-3 py-2 font-medium">Category</th>
              <th className="text-left px-3 py-2 font-medium">Prov</th>
              <th className="text-left px-3 py-2 font-medium">Closing</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Docs</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(o => (
              <tr
                key={o.id}
                className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                onClick={() => navigate(`/opportunities/${o.id}`)}
                data-testid={`opp-row-${o.rfp_id}`}
              >
                <td className="px-4 py-3 align-top"><RelevanceBadge score={o.relevance_score} /></td>
                <td className="px-3 py-3 align-top max-w-md">
                  <div className="font-medium text-zinc-900 leading-snug">{o.rfp_title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{o.buyer_name}</div>
                </td>
                <td className="px-3 py-3 align-top text-xs font-mono">{o.source_platform}</td>
                <td className="px-3 py-3 align-top text-xs">{o.category_primary}</td>
                <td className="px-3 py-3 align-top font-mono text-xs">{o.province}</td>
                <td className="px-3 py-3 align-top">
                  <FmtDate value={o.closing_deadline} />
                  {typeof o.days_remaining === "number" && (
                    <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                      {o.days_remaining >= 0 ? `${o.days_remaining}d remaining` : `${Math.abs(o.days_remaining)}d ago`}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  <StatusBadge status={o.status} active={o.active_flag} days={o.days_remaining} />
                </td>
                <td className="px-3 py-3 align-top text-right font-mono text-xs">
                  {o.document_count || 0}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-zinc-500 text-sm">No matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
